import React, { useEffect, useRef, useState } from 'react';
import { PenLine, Upload, Keyboard } from 'lucide-react';
import SignaturePad from 'signature_pad';

import useEscapeToClose from './useEscapeToClose';

// What counts as tabbable inside the modal (for the focus trap).
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Trim transparent margins so the signature fills its field box nicely.
export const cropToContent = (canvas, padding = 6) => {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let has = false;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        has = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!has) return null;
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(width - 1, maxX + padding);
  maxY = Math.min(height - 1, maxY + padding);
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out
    .getContext('2d')
    .drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
};

export const renderTypedSignature = (name) => {
  const c = document.createElement('canvas');
  c.width = 600;
  c.height = 160;
  const ctx = c.getContext('2d');
  let size = 76;
  ctx.font = `${size}px "Great Vibes", "Brush Script MT", cursive`;
  while (ctx.measureText(name).width > c.width - 40 && size > 24) {
    size -= 4;
    ctx.font = `${size}px "Great Vibes", "Brush Script MT", cursive`;
  }
  ctx.fillStyle = '#111827';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(name, c.width / 2, c.height / 2);
  return (cropToContent(c) || c).toDataURL('image/png');
};

// Load an image file, downscale to maxWidth (preserving aspect), crop any
// transparent margins (helps PNG signatures), and return a PNG data URL. Keeps
// uploaded signatures small enough to live inline in the template JSON.
export const imageFileToDataUrl = (file, maxWidth = 600) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not load the image'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(img.width * scale));
        c.height = Math.max(1, Math.round(img.height * scale));
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        resolve((cropToContent(c) || c).toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

// Capture a signature by Draw, Type, or (when allowUpload) Upload. Emits a PNG
// data URL via onApply. Shared by the external signer (draw/type) and the admin
// template builder (static signatures, with upload enabled).
const SignatureModal = ({
  onClose,
  onApply,
  initialValue,
  allowUpload = false,
  // Reopen in the mode last used for this field (typed signatures are saved as a
  // PNG, so the mode/name can't be inferred from the value — the caller remembers).
  initialMode = 'draw',
  initialTyped = '',
}) => {
  const [mode, setMode] = useState(initialMode);
  const [typedName, setTypedName] = useState(initialTyped);
  const [hasInk, setHasInk] = useState(false);
  const [existing, setExisting] = useState(initialValue || null);
  const [uploaded, setUploaded] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  // A dot or a tap was submitted as a signature.
  const [tooSmall, setTooSmall] = useState(false);
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const panelRef = useRef(null);

  useEscapeToClose(onClose);

  // Focus trap: move focus into the modal on open, wrap Tab within it, and
  // return focus to whatever opened it on close.
  useEffect(() => {
    const panel = panelRef.current;
    const previouslyFocused = document.activeElement;
    // Prefer a real text input (type mode) over the corner mode toggles.
    const initial =
      panel?.querySelector('input:not([type="file"]), textarea') ||
      panel?.querySelector(FOCUSABLE_SELECTOR);
    initial?.focus();
    const onKey = (e) => {
      if (e.key !== 'Tab' || !panel) return;
      const nodes = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (typeof previouslyFocused?.focus === 'function') previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    if (mode !== 'draw') return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const size = () => {
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
    };
    size();
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgba(0,0,0,0)',
      penColor: '#111827',
    });
    padRef.current = pad;
    setHasInk(false);
    setTooSmall(false);
    if (typeof pad.addEventListener === 'function') {
      pad.addEventListener('beginStroke', () => {
        setHasInk(true);
        setTooSmall(false);
      });
    }
    // Turning the phone resizes the pad (Nic 2026-10-24): strokes drawn on
    // the old size would be stretched, so the pad clears and they sign again
    // (signature_pad's own advice). Only a real width change counts, not the
    // address bar sliding.
    let lastW = canvas.offsetWidth;
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            const w = canvas.offsetWidth;
            if (!w || Math.abs(w - lastW) < 2) return;
            lastW = w;
            size();
            pad.clear();
            setHasInk(false);
          })
        : null;
    if (observer) observer.observe(canvas);
    return () => {
      if (observer) observer.disconnect();
      pad.clear();
      padRef.current = null;
    };
  }, [mode]);

  const clear = () => {
    if (padRef.current) padRef.current.clear();
    setExisting(null);
    setHasInk(false);
    setTypedName('');
    setUploaded(null);
    setUploadError(null);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      setUploadError('Please choose a PNG or JPG image.');
      return;
    }
    try {
      const dataUrl = await imageFileToDataUrl(file);
      setUploaded(dataUrl);
    } catch (err) {
      setUploadError(err.message || 'Could not load that image.');
    }
  };

  const canSign =
    mode === 'draw' ? hasInk : mode === 'type' ? typedName.trim().length > 0 : Boolean(uploaded);

  const handleSign = async () => {
    let dataUrl = null;
    if (mode === 'draw') {
      if (padRef.current && !padRef.current.isEmpty()) {
        const cropped = cropToContent(canvasRef.current);
        // A tap or a dot isn't a signature (Nic 2026-10-24): the ink (the
        // crop minus its 6px padding each side) must span at least 12 CSS px
        // one way or the other.
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const inkW = cropped ? (cropped.width - 12) / ratio : 0;
        const inkH = cropped ? (cropped.height - 12) / ratio : 0;
        if (!cropped || (inkW < 12 && inkH < 12)) {
          setTooSmall(true);
          return;
        }
        dataUrl = cropped.toDataURL('image/png');
      }
    } else if (mode === 'type' && typedName.trim()) {
      // Make sure the script webfont has actually loaded before burning the
      // typed name to a canvas — otherwise the PNG can race it and render in
      // the fallback font. Non-fatal if the font can't load.
      if (document.fonts?.load) {
        await document.fonts.load('16px "Great Vibes"').catch(() => {});
      }
      dataUrl = renderTypedSignature(typedName.trim());
    } else if (mode === 'upload' && uploaded) {
      dataUrl = uploaded;
    }
    if (dataUrl) onApply(dataUrl, { mode, typedName: typedName.trim() });
  };

  // Small ghost icon to switch capture mode (replaces the old tab bar).
  const ModeToggle = ({ to, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setMode(to)}
      title={label}
      aria-label={label}
      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="fixed inset-0 z-[1080] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div ref={panelRef} className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-4 shadow-xl">
        {/* One signature surface; mode toggles tucked into the corner. */}
        <div className="relative h-40 w-full overflow-hidden rounded-xl border border-border bg-background">
          <div className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5">
            {mode !== 'draw' && <ModeToggle to="draw" icon={PenLine} label="Draw signature" />}
            {mode !== 'type' && <ModeToggle to="type" icon={Keyboard} label="Type signature" />}
            {allowUpload && mode !== 'upload' && (
              <ModeToggle to="upload" icon={Upload} label="Upload signature" />
            )}
          </div>

          {mode === 'draw' ? (
            <>
              <canvas
                ref={canvasRef}
                aria-label="Signature drawing area"
                className="h-full w-full touch-none"
                onPointerUp={() => setHasInk(!padRef.current?.isEmpty())}
              />
              {existing && !hasInk ? (
                <img src={existing} alt="signature" className="pointer-events-none absolute inset-0 m-auto max-h-full max-w-full object-contain p-2" />
              ) : !hasInk ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                  <PenLine className="mr-2 h-4 w-4" /> Draw your signature
                </div>
              ) : null}
            </>
          ) : mode === 'type' ? (
            <input
              autoFocus
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your name"
              className="h-full w-full bg-transparent px-10 text-center text-4xl text-foreground placeholder:font-sans placeholder:text-base placeholder:text-muted-foreground focus:outline-none"
              style={{ fontFamily: '"Great Vibes", "Brush Script MT", cursive' }}
            />
          ) : (
            <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center p-3 text-center hover:bg-accent/40">
              {uploaded ? (
                <img src={uploaded} alt="uploaded signature" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                  <Upload className="h-5 w-5" />
                  Click to upload a PNG or JPG
                </span>
              )}
              <input type="file" accept="image/png,image/jpeg" onChange={handleUpload} className="hidden" />
            </label>
          )}
        </div>
        {uploadError ? <p className="mt-2 text-xs text-destructive">{uploadError}</p> : null}
        {tooSmall && mode === 'draw' ? (
          <p role="alert" className="mt-2 text-xs text-destructive">
            That looks like a dot. Please draw your full signature.
          </p>
        ) : null}

        <div className="mt-3 flex items-center justify-between">
          <button type="button" onClick={clear} className="rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
            Clear
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <button type="button" disabled={!canSign} onClick={handleSign} className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-40">
              {allowUpload ? 'Apply' : 'Sign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
